import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { Card, Col, DatePicker, Input, message, Row, Space, Spin, Table, Tooltip } from 'antd'
import {
  Button,
  ButtonActionBar,
  EmptyScreen,
  KPIValue,
  PeriodNavigator,
  TableFilterDropdown,
  Typography,
} from 'modules/core/components'
import {
  DownloadOutlined,
  InfoCircleOutlined,
  LeftCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import FilterSelect from 'modules/core/components/FiltersContainer/FilterSelect'
import { SiderLayout } from 'modules/core/layouts'
import {
  BALANCE_SLOTS,
  BALANCE_SLOT_LABELS,
  buildDocumentTypeCards,
  COMMITTED_COLUMN_SLOT,
  COMMITTED_DETAIL_COLUMNS,
  COMMITTED_DETAIL_WIDTH,
  extractDimensions,
  IDENTITY_CELL,
  findDimensionFor,
  findDocumentTypeLabel,
  formatSapDate,
  SUPPLIER_SLOT,
  toCommittedBody,
  toCommittedCriteria,
  emptyTableFilters,
  TABLE_FILTER_SLOTS,
  DEFAULT_DATE_ORDER,
  toDateRangeParams,
  toFilterOption,
  toOrderingParam,
  toFilterOptions,
  toTableFilterParams,
} from 'modules/core/utils/committedBalanceHelper'
import { generateMessageError } from 'modules/core/utils'
import configuration from 'modules/configuration'
import planning from 'modules/planning'
import emptyStateImage from 'assets/images/compositions/empty-big.svg'
import moment from 'moment'
import numeral from 'numeral'
import _ from 'lodash'
import './CommittedBalanceHome.scss'

const DETAIL_PAGE_SIZE = 20
const CRITERIA_HELP =
  'Definen qué información se trae del comprometido. Si los cambiás, presioná Consultar para volver a ejecutar.'
const TABLE_FILTER_HELP =
  'Acotan la información ya consultada, sin volver a ejecutar. Recalculan los KPIs y el total.'
const FILTER_PROPS = {
  mode: 'multiple',
  maxTagCount: 1, // the first chosen description shows in full ('responsive' hid it as "+1")
  maxTagTextLength: 60,
  style: { flex: '1 1 auto', minWidth: 150, maxWidth: 520 },
  dropdownMatchSelectWidth: false, // the list grows to the longest description
  dropdownStyle: { maxWidth: 'calc(100vw - 32px)' },
}
const CARD_TONES = [
  { tint: '#eef4ff', accent: '#2563eb' },
  { tint: '#e7edf8', accent: '#1e4694' },
  { tint: '#f0f2f5', accent: '#5b6675' },
  { tint: '#eaeff7', accent: '#3a5a8c' },
  { tint: '#f5f6f8', accent: '#8a94a3' },
  { tint: '#e9ecf2', accent: '#4a5568' },
]
const DETAIL_FILTERABLE = new Set([
  ...TABLE_FILTER_SLOTS.map(({ column }) => column),
  'document_number',
])
const DOCUMENT_TYPE_SLOT = 'document_type'

const emptySummary = () => ({
  hasQueried: false,
  total: 0,
  rowsCount: 0,
  byDocumentType: {},
  documentTypes: [],
})

const emptyDetail = () => ({ rows: [], total: 0, page: 1, loading: false })

const CommittedBalanceHome = ({
  fetchAllDimensions,
  fetchAllDimensionsValues,
  fetchGlobalBudgetDetail,
  globalBudgetDetail,
  getCommittedDetail,
  getCommittedSummary,
  getCommittedSuppliers,
  getCommittedSyncStatus,
  getCommittedTableOptions,
  downloadCommittedXLS,
}) => {
  const { periodId, globalBudgetId } = useParams()
  const location = useLocation()
  const history = useHistory()
  const initialFilters = location.state?.filters
  const [dimensionValues, setDimensionValues] = useState({})
  const [filters, setFilters] = useState(initialFilters ?? {})
  const [tableFilters, setTableFilters] = useState(emptyTableFilters())
  const [documentNumberDraft, setDocumentNumberDraft] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)
  const [dateRange, setDateRange] = useState(null)
  const [tableOptions, setTableOptions] = useState({})
  const [optionsLoading, setOptionsLoading] = useState({})
  const [openFilterColumn, setOpenFilterColumn] = useState(null)
  const [summary, setSummary] = useState(emptySummary())
  const [detail, setDetail] = useState(emptyDetail())
  const [dateOrder, setDateOrder] = useState(DEFAULT_DATE_ORDER) // only Fecha sorts, server-side
  const [isSummaryLoading, setIsSummaryLoading] = useState(false)
  const [sync, setSync] = useState({})
  const [isSyncLoading, setIsSyncLoading] = useState(true)
  const [isDataLoading, setIsDataLoading] = useState(false)
  const summaryRequestId = useRef(0)
  const detailRequestId = useRef(0)
  const autoSearched = useRef(false)
  const appliedTableFilters = useRef(emptyTableFilters())

  useEffect(() => {
    fetchGlobalBudgetDetail(globalBudgetId)
  }, [fetchGlobalBudgetDetail, globalBudgetId])

  useEffect(() => {
    let cancelled = false
    getCommittedSyncStatus()
      .then((response) => {
        if (!cancelled) setSync(response.payload.data)
      })
      .catch(() => {
        if (!cancelled) setSync({})
      })
      .finally(() => {
        if (!cancelled) setIsSyncLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [getCommittedSyncStatus])

  useEffect(() => {
    let cancelled = false
    setIsDataLoading(true)
    fetchAllDimensions()
      .then(async (response) => {
        const dimensions = extractDimensions(response)
        const values = await Promise.all(
          BALANCE_SLOTS.map(async (slot) => {
            if (slot === SUPPLIER_SLOT) {
              const suppliers = await getCommittedSuppliers({
                overall_budget_id: globalBudgetId,
              }).catch(() => null)
              return [
                slot,
                (suppliers?.payload?.data ?? []).map(({ code, name }) =>
                  toFilterOption(code, name || 'Sin nombre en la dimensión'),
                ),
              ]
            }
            const dimension = findDimensionFor(slot, dimensions)
            const dimensionId = dimension?.id ?? dimension?.id_dimension
            if (dimensionId == null) return [slot, []]
            const valuesResponse = await fetchAllDimensionsValues({ id_dimension: dimensionId })
            return [slot, toFilterOptions(valuesResponse?.payload?.data ?? [])]
          }),
        )
        if (!cancelled) setDimensionValues(_.fromPairs(values))
      })
      .catch(() => {
        if (!cancelled) setDimensionValues({})
      })
      .finally(() => {
        if (!cancelled) setIsDataLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fetchAllDimensions, fetchAllDimensionsValues, getCommittedSuppliers, globalBudgetId])

  const hasAnyFilter =
    BALANCE_SLOTS.some((slot) => !_.isEmpty(filters[slot])) || !_.isEmpty(dateRange)

  const resetSummary = () => {
    summaryRequestId.current += 1
    detailRequestId.current += 1
    setSummary(emptySummary())
    setDetail(emptyDetail())
    setTableFilters(emptyTableFilters())
    setDocumentNumberDraft('')
    setTableOptions({})
    setOptionsLoading({})
    appliedTableFilters.current = emptyTableFilters()
    setIsSummaryLoading(false)
  }

  const onSelectItems = (values, slot) => {
    setFilters((prevState) => ({ ...prevState, [slot]: values }))
    resetSummary()
  }

  const applyTableFilters = (next) => {
    if (!_.isEqual(appliedTableFilters.current, next)) onSearch(filters, next)
  }

  const onChangeTableFilter = (column, values) => {
    const next = { ...tableFilters, [column]: values }
    setTableFilters(next)
    applyTableFilters(next)
  }

  const onFilterDropdownVisible = (column, isOpen) => {
    if (!isOpen) return setOpenFilterColumn(null)
    if (tableOptions[column]) return setOpenFilterColumn(column)
    if (optionsLoading[column]) return
    fetchTableOptions(column).then((loaded) => loaded && setOpenFilterColumn(column))
  }

  const onChangeDateRange = (values) => {
    setDateRange(values)
    resetSummary()
  }

  const onDownload = () => {
    setIsDownloading(true)
    downloadCommittedXLS({
      overall_budget_id: globalBudgetId,
      ...toCommittedCriteria(filters),
      ...toDateRangeParams(dateRange),
      ...toTableFilterParams(tableFilters),
      ordering: toOrderingParam(dateOrder),
    })
      .catch((error) => message.error(generateMessageError(error), 8))
      .finally(() => setIsDownloading(false))
  }

  const onClean = () => {
    setFilters({})
    setDateRange(null)
    resetSummary()
  }

  const fetchDetail = (
    page,
    activeFilters = filters,
    activeTable = tableFilters,
    activeOrder = dateOrder,
  ) => {
    const requestId = ++detailRequestId.current
    setDetail((prev) => ({ ...prev, page, loading: true }))
    getCommittedDetail({
      overall_budget_id: globalBudgetId,
      ...toCommittedCriteria(activeFilters),
      ...toDateRangeParams(dateRange),
      ...toTableFilterParams(activeTable),
      ordering: toOrderingParam(activeOrder),
      page,
      page_size: DETAIL_PAGE_SIZE,
    })
      .then((response) => {
        if (requestId !== detailRequestId.current) return
        const data = response.payload.data
        setDetail({ rows: data.results ?? [], total: data.count ?? 0, page, loading: false })
      })
      .catch((error) => {
        if (requestId !== detailRequestId.current) return
        message.error(generateMessageError(error), 8)
        setDetail({ ...emptyDetail(), page })
      })
  }

  const fetchTableOptions = (column) => {
    const requestId = summaryRequestId.current
    setOptionsLoading((prevState) => ({ ...prevState, [column]: true }))
    return getCommittedTableOptions({
      overall_budget_id: globalBudgetId,
      ...toCommittedBody(filters),
      ...toDateRangeParams(dateRange),
      column,
    })
      .then((response) => {
        if (requestId !== summaryRequestId.current) return false
        const data = response.payload.data
        setTableOptions((prevState) => ({ ...prevState, [data.column]: data.options }))
        return true
      })
      .catch((error) => {
        if (requestId === summaryRequestId.current) message.error(generateMessageError(error), 8)
        return false
      })
      .finally(() => setOptionsLoading((prevState) => ({ ...prevState, [column]: false })))
  }

  const onTableChange = (pagination, tableFilterValues, sorter, extra) => {
    if (extra.action !== 'sort') return
    const nextOrder = sorter.order ?? DEFAULT_DATE_ORDER
    setDateOrder(nextOrder)
    fetchDetail(1, filters, tableFilters, nextOrder)
  }

  const onSearch = (activeFilters = filters, activeTable = tableFilters) => {
    const requestId = ++summaryRequestId.current
    appliedTableFilters.current = activeTable
    setIsSummaryLoading(true)
    fetchDetail(1, activeFilters, activeTable)
    getCommittedSummary({
      overall_budget_id: globalBudgetId,
      ...toCommittedBody(activeFilters),
      ...toDateRangeParams(dateRange),
      ...toTableFilterParams(activeTable),
    })
      .then((response) => {
        if (requestId !== summaryRequestId.current) return
        const data = response.payload.data
        const byDocumentType = _.keyBy(data.by_document_type, 'document_type')
        setSummary({
          hasQueried: true,
          total: data.total,
          rowsCount: data.rows_count,
          byDocumentType,
          documentTypes: data.document_types,
        })
      })
      .catch((error) => {
        if (requestId !== summaryRequestId.current) return
        message.error(generateMessageError(error), 8)
        setSummary(emptySummary())
      })
      .finally(() => {
        if (requestId === summaryRequestId.current) setIsSummaryLoading(false)
      })
  }

  useEffect(() => {
    if (autoSearched.current || !initialFilters) return
    autoSearched.current = true
    onSearch(initialFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters])

  const globalBudgetUrl = `/planificacion/${periodId}/presupuestoglobal/${globalBudgetId}`

  const renderReturn = () => (
    <Space direction="horizontal">
      <Button.Icon
        title="ACTION_RETURN"
        icon={<LeftCircleOutlined style={{ fontSize: 20, color: 'var(--absolute-blue-6)' }} />}
        onClick={() => history.push(globalBudgetUrl)}
      />
      <Typography.Body>Volver a presupuesto</Typography.Body>
    </Space>
  )

  const renderHeader = () => (
    <PeriodNavigator
      items={[
        { name: 'Planificación', url: `/planificacion/${periodId}/presupuestoglobal` },
        {
          name: globalBudgetDetail?.name,
          url: globalBudgetUrl,
        },
        { name: 'Consultar comprometido' },
      ]}
    />
  )

  const renderSyncDate = () => {
    if (isSyncLoading) return null

    if (!sync.last_synced_at)
      return (
        <div className="committed-total-sync">
          <Typography.Body level={3} className="committed-total-card-legend">
            El comprometido todavía no se sincronizó con SAP
          </Typography.Body>
        </div>
      )

    return (
      <div className="committed-total-sync">
        <Typography.Body
          level={3}
          className="committed-total-card-legend committed-total-sync-title"
        >
          Última sincronización con SAP
        </Typography.Body>
        <Typography.Body level={2} className="committed-total-sync-date">
          {moment(sync.last_synced_at).format('DD/MM/YYYY · HH:mm')}
        </Typography.Body>
        <Typography.Body level={3} className="committed-total-card-legend">
          Acumulado de los meses del período
        </Typography.Body>
      </div>
    )
  }

  const renderFilterGroupLabel = (title, help) => (
    <Space size={4}>
      <Typography.Body level={3} type="secondary">
        {title}
      </Typography.Body>
      <Tooltip title={help}>
        <Typography.Icon icon={InfoCircleOutlined} />
      </Tooltip>
    </Space>
  )

  const renderFilters = () => (
    <>
      <div style={{ marginBottom: 8 }}>
        {renderFilterGroupLabel('Panel de consulta', CRITERIA_HELP)}
      </div>
      <div className="committed-panel-filters">
        {BALANCE_SLOTS.map((slot) => (
          <FilterSelect
            key={slot}
            label={BALANCE_SLOT_LABELS[slot]}
            dimensionId={slot}
            onChangeSelection={onSelectItems}
            selection={filters}
            options={dimensionValues[slot] ?? []}
            isValuesLoading={isDataLoading}
            props={FILTER_PROPS}
          />
        ))}
        <DatePicker.RangePicker
          format="DD/MM/YYYY"
          style={{ width: 240 }}
          placeholder={['Fecha desde', 'Fecha hasta']}
          value={dateRange}
          onChange={onChangeDateRange}
        />
      </div>
      <ButtonActionBar className="committed-panel-actions">
        <Space size="small">
          <Button.Default title="Limpiar filtros" disabled={!hasAnyFilter} onClick={onClean} />
          <Button.Primary
            title="Consultar"
            loading={isSummaryLoading}
            disabled={!hasAnyFilter}
            tooltipTitle={
              !hasAnyFilter
                ? 'Seleccioná al menos un valor o un rango de fechas para consultar'
                : ''
            }
            onClick={() => onSearch()}
          />
        </Space>
      </ButtonActionBar>
    </>
  )

  const renderKpiCard = ({ label, amount, count }, index) => {
    const tone = CARD_TONES[index % CARD_TONES.length]
    return (
      <Card
        size="small"
        className="committed-type-card"
        style={{ '--tone-accent': tone.accent, '--tone-tint': tone.tint }}
      >
        <Typography.Body level={3}>{label}</Typography.Body>
        <KPIValue
          size="medium"
          prefix="$"
          fullValue={numeral(amount).format('0,0')}
          value={numeral(amount).format('0[.]0a')}
        />
        <Typography.Body level={3} type="secondary">
          {`${count} registros`}
        </Typography.Body>
      </Card>
    )
  }

  const renderKpis = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card size="small" className="committed-total-card">
          <div>
            {/* Sin abreviar: es la unica cifra de la pantalla que el usuario puede necesitar
                leer al peso, y hay ancho de sobra para mostrarla entera. */}
            <Typography.Headline level={3}>
              {`$ ${numeral(summary.total).format('0,0')}`}
            </Typography.Headline>
            <Typography.Body level={3} className="committed-total-card-legend">
              {summary.hasQueried
                ? `Comprometido total + reserva DPD · ${summary.rowsCount} registros · COP`
                : 'Comprometido total + reserva DPD'}
            </Typography.Body>
          </div>
          {renderSyncDate()}
        </Card>
      </Col>
      {buildDocumentTypeCards(summary.byDocumentType, summary.documentTypes).map(
        (documentType, index) => (
          <Col span={4} key={documentType.value}>
            {renderKpiCard(documentType, index)}
          </Col>
        ),
      )}
    </Row>
  )

  const nameBySlot = (slot, code) => {
    if (!code) return code
    const match = (dimensionValues[slot] ?? []).find((value) => value.id === code)
    return match?.name ?? code
  }

  const cellText = (key, value, amount) => {
    if (amount) return numeral(Number(value)).format('0,0')
    if (key === 'date') return formatSapDate(value)
    if (key === DOCUMENT_TYPE_SLOT) return findDocumentTypeLabel(summary.documentTypes, value)
    if (COMMITTED_COLUMN_SLOT[key]) return nameBySlot(COMMITTED_COLUMN_SLOT[key], value)
    return value
  }

  const renderDocumentNumberFilter = ({ confirm }) => (
    <div style={{ padding: 8 }}>
      <Input.Search
        allowClear
        style={{ width: 220 }}
        placeholder="Nº de documento"
        value={documentNumberDraft}
        onChange={(event) => setDocumentNumberDraft(event.target.value)}
        onSearch={(value) => {
          onChangeTableFilter('documentNumber', value.trim())
          confirm()
        }}
      />
    </div>
  )

  const columnFilter = (key) => {
    if (!DETAIL_FILTERABLE.has(key)) return {}
    if (key === 'document_number')
      return {
        filteredValue: tableFilters.documentNumber ? [tableFilters.documentNumber] : null,
        filterDropdown: renderDocumentNumberFilter,
      }
    return {
      filteredValue: _.isEmpty(tableFilters[key]) ? null : tableFilters[key],
      filterIcon: optionsLoading[key] ? <LoadingOutlined /> : undefined, // default icon otherwise
      filterDropdownVisible: openFilterColumn === key,
      onFilterDropdownVisibleChange: (isOpen) => onFilterDropdownVisible(key, isOpen),
      filterDropdown: (filterDropdownProps) => (
        <TableFilterDropdown
          {...filterDropdownProps}
          filters={(tableOptions[key] ?? []).map((option) => ({
            id: option.value,
            name: `${option.label} (${option.count})`,
          }))}
          filteredValues={tableFilters[key]}
          onFilter={(values) => onChangeTableFilter(key, values)}
        />
      ),
    }
  }

  const detailColumns = COMMITTED_DETAIL_COLUMNS.map(
    ({ key, title, width, align, ellipsis, tooltip, amount, fixed, className }) => ({
      title,
      dataIndex: key,
      key,
      width,
      align,
      fixed,
      className,
      ...columnFilter(key),
      ...(key === 'date' && {
        sorter: true,
        sortOrder: dateOrder,
        sortDirections: ['ascend', 'descend', 'ascend'],
        showSorterTooltip: false,
      }),
      ellipsis: tooltip ? { showTitle: false } : ellipsis,
      render: (value) => {
        const text = cellText(key, value, amount)
        const content = amount || key === DOCUMENT_TYPE_SLOT ? <strong>{text}</strong> : text
        if (tooltip)
          return (
            <Tooltip placement="topLeft" title={text}>
              {content}
            </Tooltip>
          )
        return content
      },
    }),
  )

  const renderDetailTableFilter = () => (
    <ButtonActionBar position="space-between">
      {renderFilterGroupLabel('Filtros de la tabla', TABLE_FILTER_HELP)}
      <Button.Icon
        title="ACTION_DOWNLOAD_TABLE"
        icon={<Typography.Icon icon={DownloadOutlined} />}
        loading={isDownloading}
        onClick={onDownload}
      />
    </ButtonActionBar>
  )

  const renderDetailTotal = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={2} className={IDENTITY_CELL}>
        <strong>Total comprometido (filtros aplicados)</strong>
      </Table.Summary.Cell>
      {COMMITTED_DETAIL_COLUMNS.slice(2, -1).map((column, offset) => (
        <Table.Summary.Cell key={column.key} index={offset + 2} className={column.className} />
      ))}
      <Table.Summary.Cell index={COMMITTED_DETAIL_COLUMNS.length - 1} align="right">
        <strong>{numeral(summary.total).format('0,0')}</strong>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  )

  const renderDetailTable = () => (
    <Table
      rowKey="id"
      className="committed-detail-table"
      size="small"
      columns={detailColumns}
      dataSource={detail.rows}
      loading={detail.loading || isSummaryLoading}
      tableLayout="fixed"
      scroll={{ x: COMMITTED_DETAIL_WIDTH }}
      locale={{ emptyText: 'No hay documentos comprometidos para los criterios elegidos' }}
      summary={detail.rows.length > 0 ? renderDetailTotal : undefined}
      onChange={onTableChange}
      pagination={{
        current: detail.page,
        pageSize: DETAIL_PAGE_SIZE,
        total: detail.total,
        showSizeChanger: false,
        showTotal: (total) => `${total} registros`,
        onChange: (page) => fetchDetail(page),
      }}
    />
  )

  const renderNoQueryScreen = () => (
    <EmptyScreen
      image={emptyStateImage}
      title="Seleccioná al menos un valor y presioná Consultar"
      description="La pantalla abre sin ejecutar ninguna consulta. Elegí los criterios de arriba para traer el detalle del comprometido."
    />
  )

  return (
    <SiderLayout>
      {renderHeader()}
      <Row gutter={[24, 24]}>
        <Col span={24}>{renderReturn()}</Col>
        <Col span={24}>{renderFilters()}</Col>
        <Col span={24}>
          <Spin spinning={isSummaryLoading}>{renderKpis()}</Spin>
        </Col>
        {summary.hasQueried && (
          <Col span={24}>
            {/* Pegada a la tabla y con poco aire: ya no es una barra de filtros, son una leyenda
                y el boton de descarga. Los filtros viven en las cabeceras. */}
            <div style={{ marginBottom: 8 }}>{renderDetailTableFilter()}</div>
            {renderDetailTable()}
          </Col>
        )}
        {!summary.hasQueried && <Col span={24}>{renderNoQueryScreen()}</Col>}
      </Row>
    </SiderLayout>
  )
}

const mapStateToProps = (state) => ({
  globalBudgetDetail: planning.selectors.getGlobalBudgetDetail(state),
})

const mapDispatchToProps = {
  fetchAllDimensions: configuration.actions.fetchAllDimensions,
  fetchAllDimensionsValues: configuration.actions.fetchAllDimensionsValues,
  fetchGlobalBudgetDetail: planning.actions.fetchGlobalBudgetDetail,
  getCommittedDetail: planning.actions.getCommittedDetail,
  getCommittedSummary: planning.actions.getCommittedSummary,
  getCommittedSuppliers: planning.actions.getCommittedSuppliers,
  getCommittedSyncStatus: planning.actions.getCommittedSyncStatus,
  getCommittedTableOptions: planning.actions.getCommittedTableOptions,
  downloadCommittedXLS: planning.actions.downloadCommittedXLS,
}

export default connect(mapStateToProps, mapDispatchToProps)(CommittedBalanceHome)
